import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch, connect } from "react-redux";
import {
  Modal, Button, ModalHeader, ModalBody, UncontrolledAlert
} from "reactstrap";
import { AvForm, AvField } from "availity-reactstrap-validation";

import { withTranslation } from "react-i18next";
import { addCampaignTemplate } from "store/actions";
import AvFieldTextTag from "components/Common/AvFieldTextTag";
import { BASE_EMAIL_FIELDS } from "common/data/dropdowns";
import { languageOptions } from "constants/userEmails";
import AvFieldSelect from "components/Common/AvFieldSelect";
import TinyEditor from "components/Common/tinyEditor/Index";
import * as axiosHelper from "../../../apis/api_helper.js";

const buildEmptyContent = () =>
  languageOptions.reduce((accumulator, lang) => {
    accumulator[lang.value] = {
      subject: "",
      body: "",
    };

    return accumulator;
  }, {});

function AddTemplate(props) {
  const allCampaignTemplateTitles = props.allCampaignTemplates.map(
    (campaignTemplate) => campaignTemplate.title
  );

  const [duplicateTitle, setDuplicateTitle] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [contentByLanguage, setContentByLanguage] = useState(buildEmptyContent);
  const [uploadImageError, setUploadImageError] = useState("");
  const [language, setLanguage] = useState(languageOptions[0].value);

  const dispatch = useDispatch();
  const { create } = props.emailCampaignPermissions;

  const repeatedTitleCheck = (e) => {
    if (e.target?.value?.length > 0) {
      setDuplicateTitle(
        allCampaignTemplateTitles.includes(e.target.value.trim())
      );
    }
  };

  const titleErrorStyle = duplicateTitle
    ? "1px solid red"
    : "1px solid rgb(181, 181, 181)";

  const onEditorStateChange = (newEditorState) => {
    setContentByLanguage((prevContent) => ({
      ...prevContent,
      [language]: {
        ...prevContent[language],
        body: newEditorState,
      },
    }));
  };

  const onSubjectChange = (event) => {
    const { value } = event.target;

    setContentByLanguage((prevContent) => ({
      ...prevContent,
      [language]: {
        ...prevContent[language],
        subject: value,
      },
    }));
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append("images", file);

    const res = await axiosHelper.postFormData(
      "/campaign-templates/upload-content-file",
      formData
    );

    if (!res?.result) {
      const errorMessage = res?.message || "Image upload failed";
      setUploadImageError(errorMessage);
      throw new Error(errorMessage);
    }

    setUploadImageError("");
    return res.result;
  };

  const handleAddCampaignTemplate = (e, values) => {
    delete values.subject;

    dispatch(
      addCampaignTemplate({
        content: contentByLanguage,
        ...values,
      })
    );
  };

  const toggleAddModal = () => {
    setAddModal(!addModal);
    setDuplicateTitle(false);
    setUploadImageError("");
    setContentByLanguage(buildEmptyContent());
    setLanguage(languageOptions[0].value);
  };

  useEffect(() => {
    if (props.clearingCounter > 0 && addModal) {
      setAddModal(false);
      setDuplicateTitle(false);
      setUploadImageError("");
      setContentByLanguage(buildEmptyContent());
      setLanguage(languageOptions[0].value);
    }
  }, [props.clearingCounter]);

  useEffect(() => {
    setDuplicateTitle(false);
  }, [props.allCampaignTemplates]);

  return (
    <>
      <Link
        to="#"
        className={`btn btn-primary ${!create ? "d-none" : ""}`}
        onClick={toggleAddModal}
      >
        <i className="bx bx-plus me-1"></i> {props.t("Add Template")}
      </Link>

      <Modal isOpen={addModal} toggle={toggleAddModal} centered size="lg">
        <ModalHeader toggle={toggleAddModal} tag="h4">
          {props.t("Add New Campaign Template")}
        </ModalHeader>

        <ModalBody>
          <AvForm
            className="p-4"
            onValidSubmit={(e, v) => {
              delete v.language;

              if (!duplicateTitle) {
                v.fields = v.fields?.map((obj) => obj.value) || [];
                handleAddCampaignTemplate(e, v);
              }
            }}
          >
            <AvField
              name="title"
              label={props.t("Title")}
              placeholder={props.t("Enter Title")}
              type="text"
              errorMessage={props.t("Enter Title")}
              validate={{ required: { value: true } }}
              onChange={repeatedTitleCheck}
              style={{ border: titleErrorStyle }}
            />

            {duplicateTitle && (
              <span className="text-danger">
                This title is already in use
              </span>
            )}

            <AvFieldTextTag
              name="fields"
              label={props.t("Fields")}
              value={[]}
              type="text"
              options={BASE_EMAIL_FIELDS}
            />

            <AvFieldSelect
              name="language"
              label={props.t("Language")}
              options={languageOptions}
              value={language}
              onChange={(e) => setLanguage(e)}
              required
            />

            <AvField
              name="subject"
              label={props.t("Subject")}
              required
              value={contentByLanguage[language]?.subject || ""}
              onChange={onSubjectChange}
            />

            <small>
              {props.t("*Customer related fields must be wrapped in")} {"{{ }}"}{" "}
              {props.t("instead of")} _ _
            </small>

            {uploadImageError && (
              <UncontrolledAlert color="danger">
                <i className="mdi mdi-block-helper me-2"></i>
                {props.t(uploadImageError)}
              </UncontrolledAlert>
            )}

            <TinyEditor
              editorState={contentByLanguage[language]?.body || ""}
              onEditorStateChange={onEditorStateChange}
              uploadImage={uploadImage}
            />

            <div className="text-center pt-3 p-2">
              <Button
                disabled={props.addLoading}
                type="submit"
                color="primary"
              >
                {props.t("Add")}
              </Button>
            </div>
          </AvForm>

          {props.addError && (
            <UncontrolledAlert color="danger">
              <i className="mdi mdi-block-helper me-2"></i>
              {props.t(JSON.stringify(props.addErrorDetails))}
            </UncontrolledAlert>
          )}
        </ModalBody>
      </Modal>
    </>
  );
}

const mapStateToProps = (state) => ({
  addLoading: state.campaignTemplates.addLoading,
  addErrorDetails: state.campaignTemplates.addErrorDetails,
  addError: state.campaignTemplates.addError,
  clearingCounter: state.campaignTemplates.clearingCounter,
  emailCampaignPermissions:
    state.Profile.emailCampaignPermissions || {},
});

export default connect(mapStateToProps, null)(
  withTranslation()(AddTemplate)
);
